import { column, Css, emptyCell, GridColumn, GridDataRow, GridTable, simpleHeader } from "@homebound/beam";
import { priceCell } from "src/components/gridTableCells";
import {
  PlanBasedLineItemsTab_LineItemFragment,
  PlanBasedLineItemsTab_PlanBasedLineItemFragment,
} from "src/generated/graphql-types";

type PlanBasedBidLinesTableProps = {
  lineItems: PlanBasedLineItemsTab_LineItemFragment[];
};

export function PlanBasedBidLinesTable({ lineItems }: PlanBasedBidLinesTableProps) {
  return (
    <section data-testid="planBasedBidLinesSection">
      <div css={Css.lgSb.mb4.$}>Cost Lines</div>
      <GridTable
        columns={columns}
        rows={createRows(lineItems)}
        style={{ bordered: false, allWhite: true }}
        rowStyles={{
          header: { cellCss: Css.gray600.$ },
          total: { cellCss: Css.smSb.mt2.$ },
        }}
        fallbackMessage="No plan based bid lines"
      />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: PlanBasedLineItemsTab_PlanBasedLineItemFragment & { taxableCostInCents: number } };
type TotalRow = { kind: "total"; data: { totalCostInCents: number } };
type Row = HeaderRow | DataRow | TotalRow;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Option", data: ({ groupLine }) => groupLine.group.displayName, total: emptyCell }),
  column<Row>({
    header: "Dimensions",
    data: ({ groupLine }) => groupLine.options.map((o) => o.name).join(" "),
    total: emptyCell,
  }),
  column<Row>({
    header: "Cost",
    align: "right",
    data: ({ taxableCostInCents }) => priceCell({ valueInCents: taxableCostInCents }),
    total: ({ totalCostInCents }) => priceCell({ id: "totalCostInCents", valueInCents: totalCostInCents }),
  }),
];

function createRows(lineItems: PlanBasedLineItemsTab_LineItemFragment[]): GridDataRow<Row>[] {
  const rows = lineItems
    .filter((cli) => cli.planBasedLineItem)
    .map((cli) => ({
      kind: "data" as const,
      id: cli.planBasedLineItem!.id,
      data: { ...cli.planBasedLineItem!, taxableCostInCents: cli.taxableCostInCents },
    }));
  return [
    simpleHeader,
    ...rows,
    { id: "total", kind: "total" as const, data: { totalCostInCents: rows.sum((r) => r.data.taxableCostInCents) } },
  ];
}
